import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Analytics, setUserProperties } from '@angular/fire/analytics';
import {
	Auth,
	authState,
	EmailAuthProvider,
	getIdToken,
	getIdTokenResult,
	multiFactor,
	PhoneAuthProvider,
	PhoneMultiFactorGenerator,
	reauthenticateWithCredential,
	RecaptchaVerifier,
	updatePassword,
	User
} from '@angular/fire/auth';
import {
	doc,
	docData,
	DocumentReference,
	Firestore,
	updateDoc
} from '@angular/fire/firestore';
import { PccAuthService } from '@ss/shared/auth';
import { firebaseConfig } from '@ss/typings/config';
import { Facility, FacilityAppRoute, SimpliscreenUser, UserClaims, UserNotificationSetting } from '@ss/typings/facility';
import { PccUser } from '@ss/typings/pcc';
import { Timestamp } from 'firebase/firestore';
import { derivedAsync } from 'ngxtension/derived-async';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserStore {
	private auth = inject(Auth);

	private firestore = inject(Firestore);

	private pccAuthService = inject(PccAuthService);

	private http = inject(HttpClient);

	user = signal<User>(null);

	userInfo = signal(new SimpliscreenUser({}));

	userClaims = signal(new UserClaims({}));

	analytics = inject(Analytics);

	userLoaded: Observable<User>;

	private subject = new BehaviorSubject<SimpliscreenUser>(null);

	loaded = this.subject.asObservable().pipe(filter((u) => !!u));

	currentFacilityId = computed(() => this.userInfo().currentFacilityId);

	hasMultiFacilities = computed(() => this.userInfo().facilities?.length > 1);

	canViewEnterprise = computed(() => this.userInfo().isSuperAdmin || !!this.userInfo().enterpriseId || this.userInfo().enterpriseIds?.length > 0);

	pccUser = derivedAsync<PccUser>(() => {
		if (this.pccAuthService.isLoggedInWithPcc()) {
			return this.http.get<PccUser>(`${firebaseConfig.functionsUrl}/ntaApi/pcc/user`);
		}
		return new PccUser();
	}, { initialValue: new PccUser() });

	constructor() {
		this.userLoaded = authState(this.auth)
			.pipe(
				tap((user) => {
					this.user.set(user);
				}),
				shareReplay(1)
			);
		this.userLoaded.pipe(
			switchMap((user) => {
				if (!user) {
					this.subject.next(this.userInfo());
					this.userInfo.set(new SimpliscreenUser({}));
					this.userClaims.set(new UserClaims({}));
					return of(null);
				}

				return docData<SimpliscreenUser>(doc(this.firestore, '/users', user.uid) as DocumentReference<SimpliscreenUser>, { idField: 'userId' })
					.pipe(map((userFacilityInfo) => ({ user, userFacilityInfo })));
			}),
			filter((userData): userData is { user: User; userFacilityInfo: SimpliscreenUser } => !!userData),
			switchMap(async({ user, userFacilityInfo }) => {
				const { claimsUpdatedAt } = userFacilityInfo;
				let claimsUpdatedAtMs = 0;
				if (claimsUpdatedAt instanceof Timestamp) claimsUpdatedAtMs = claimsUpdatedAt.toMillis();
				else if (claimsUpdatedAt instanceof Date) claimsUpdatedAtMs = claimsUpdatedAt.getTime();
				let tokenResult = await getIdTokenResult(user);
				if (claimsUpdatedAtMs) {
					if (claimsUpdatedAtMs > Date.parse(tokenResult.issuedAtTime)) {
						await getIdToken(user, true);
						tokenResult = await getIdTokenResult(user);
					}
				}
				this.userClaims.set(new UserClaims(tokenResult.claims as Partial<UserClaims>));
				return { user, userFacilityInfo };
			}),
			switchMap(({ user, userFacilityInfo }) => {
				setUserProperties(this.analytics, {
					enterpriseId: userFacilityInfo.enterpriseId || 'none',
					isSuperAdmin: userFacilityInfo.isSuperAdmin ?? false,
					facilityId: userFacilityInfo.currentFacilityId || 'none'
				});
				this.userInfo.set({
					email: user.email,
					name: user.displayName,
					phoneNumber: user.phoneNumber,
					...userFacilityInfo,
				});
				if (userFacilityInfo?.facilityIds?.length === 0) {
					return of([]);
				}
				return combineLatest(userFacilityInfo?.facilityIds.map((facilityId) => {
					const facilityDoc = doc(this.firestore, 'facilities', facilityId) as DocumentReference<Facility>;
					return docData<Facility>(facilityDoc, { idField: 'facilityId' });
				}));
			})
		).subscribe((facilities) => {
			this.userInfo.update((u) => {
				const facility = u.facilities?.find((f) => f.facilityId === u.currentFacilityId);
				const facilityDetails = facilities.filter((f) => f).map((f) => ({ name: f.name, facilityId: f.facilityId, ntaEnabled: f.ntaEnabled }));
				return {
					...u,
					facility,
					facilities: facilityDetails,
				};
			});
			this.subject.next(this.userInfo());
		});
	}

	reauthenticate(username: string, password: string) {
		const credential = EmailAuthProvider.credential(username, password);

		return reauthenticateWithCredential(this.user(), credential);
	}

	async saveUserInfo(userUpdate: { name: string; email: string }) {
		await updateDoc(doc(this.firestore, `/users/${this.user().uid}`), { ...userUpdate });
	}

	async saveNotificationSettings(notificationSettings: UserNotificationSetting[]) {
		notificationSettings = notificationSettings.map((n) => Object.keys(n).reduce((acc, key) => {
			if (n[key] !== undefined) {
				acc[key] = n[key];
			}
			return acc;
		}, {} satisfies UserNotificationSetting));
		await updateDoc(doc(this.firestore, `/users/${this.user().uid}`), {
			notificationSettings: notificationSettings.map((n) => Object.keys(n).reduce((acc, key) => {
				if (n[key] !== undefined) {
					acc[key] = n[key];
				}
				return acc;
			}, {}))
		});
	}

	updateFacility(facilityId: string, lastFacilityAppRoute?: FacilityAppRoute) {
		const update: Pick<SimpliscreenUser, 'currentFacilityId'> & Pick<Partial<SimpliscreenUser>, 'lastFacilityAppRoute'> = { currentFacilityId: facilityId };
		if (lastFacilityAppRoute) update.lastFacilityAppRoute = lastFacilityAppRoute;
		return from(updateDoc(doc(this.firestore, `/users/${this.user().uid}`), update));
	}

	updateLastFacilityAppRoute(lastFacilityAppRoute: FacilityAppRoute) {
		if (!this.user()) return of(undefined);
		if (this.userInfo().lastFacilityAppRoute === lastFacilityAppRoute) return of(undefined);
		return from(updateDoc(doc(this.firestore, `/users/${this.user().uid}`), { lastFacilityAppRoute }));
	}

	async updateTempPassword(newPassword: string, currentPassword: string) {
		await this.reauthenticate(this.user().email, currentPassword);
		await updatePassword(this.user(), newPassword);
		await updateDoc(doc(this.firestore, `/users/${this.user().uid}`), { hasTempPassword: false });
	}

	async sendCode(password: string, phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) {
		await firstValueFrom(this.userLoaded);
		await this.reauthenticate(this.user().email, password);
		const session = await multiFactor(this.user()).getSession();
		const phoneInfoOptions = { phoneNumber, session };
		const phoneAuthProvider = new PhoneAuthProvider(this.auth);
		const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
		return verificationId;
	}

	validateCode(verificationId: string, verificationCode: string) {
		const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
		const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

		return multiFactorAssertion;
	}

	async enableMfa(verificationId: string, verificationCode: string) {
		const multiFactorAssertion = this.validateCode(verificationId, verificationCode);

		await multiFactor(this.user()).enroll(multiFactorAssertion);

		return updateDoc(doc(this.firestore, `/users/${this.user().uid}`), { mfaEnabled: true });
	}
}
